/**
 * escpos-ascii — Translittération ASCII 7 bits pour buffers ESC/POS.
 *
 * Les imprimantes thermiques interprètent les octets >= 0x80 selon leur
 * codepage actif : CP437 sur les BT génériques, GBK sur les Sunmi
 * (InnerPrinter exposée en BT). Envoyer de l'UTF-8 brut y produit des
 * caractères chinois — é (0xC3 0xA9) s'imprime 茅 (constaté prod
 * Hôtel des Voyageurs 04/08/2026). La seule sortie sûre sur tout le parc
 * est l'ASCII : accents décomposés puis dépouillés, symboles courants
 * remplacés, tout caractère non mappé devient '?'.
 *
 * Les caractères de contrôle (< 0x20 : ESC, GS, LF…) traversent intacts —
 * les séquences ESC/POS embarquées dans la chaîne sont préservées.
 *
 * Module pur (règle lib/) — même translittération que le ping BT validé
 * NT311 (ex-cp437 de bt/escpos.ts, extraite ici pour partage).
 */

/** Translittère une chaîne vers son équivalent ASCII imprimable. */
export function toEscPosAscii(text: string): string {
  // Décomposer puis retirer les diacritiques → ASCII propre
  const normalized = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Remplacements spéciaux (espaces Unicode, ligatures, symboles, guillemets)
  return (
    normalized
      // Espaces Unicode → espace ASCII. Les ICU récentes (Chromium >= 110)
      // émettent U+202F/U+00A0 dans toLocaleString('fr-FR') — non mappés,
      // ils s'imprimaient '?' à la place des espaces (constaté prod
      // AUX DELICES MUROIS 09/2026, tickets BT CloudPrint).
      .replace(/[\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000]/g, ' ')
      // Invisibles zéro-largeur (copier-coller mobile) → supprimés
      .replace(/[\u200b-\u200d\u2060\ufeff]/g, '')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201c\u201d]/g, '"')
      .replace(/œ/g, 'oe')
      .replace(/Œ/g, 'OE')
      .replace(/æ/g, 'ae')
      .replace(/Æ/g, 'AE')
      .replace(/€/g, 'EUR')
      .replace(/[–—]/g, '-')
      .replace(/·/g, '-')
      .replace(/…/g, '...')
      .replace(/°/g, 'o')
  );
}

/**
 * Encode une chaîne (texte + séquences ESC/POS) en octets ASCII.
 * Tout caractère encore hors ASCII après translittération devient '?'.
 */
export function encodeEscPosAscii(text: string): Uint8Array {
  const cleaned = toEscPosAscii(text);
  const out: number[] = [];
  for (const ch of cleaned) {
    const code = ch.charCodeAt(0);
    out.push(code < 128 ? code : 0x3f); // '?' pour tout non-ASCII restant
  }
  return new Uint8Array(out);
}
